import { useState, useCallback, useEffect, useOptimistic } from 'react';

const useChatSessions = ({ api, token, onSessionChange }) => {
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);

    // Use optimistic updates for session changes
    const [optimisticSessions, updateOptimisticSessions] = useOptimistic(
        sessions,
        (currentSessions, optimisticValue) => {
            return currentSessions.map(session =>
                session.session_id === optimisticValue.sessionId
                    ? { ...session, title: optimisticValue.title, updated_at: new Date().toISOString() }
                    : session
            );
        }
    );

    const fetchSessions = useCallback(async () => {
        if (!token) return;

        setIsLoadingSessions(true);
        try {
            const response = await fetch(`${api}/sessions`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch sessions');
            }

            const data = await response.json();
            setSessions(data.sessions || []);
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
        } finally {
            setIsLoadingSessions(false);
        }
    }, [api, token]);

    useEffect(() => {
        if (token) fetchSessions();
    }, [token]);

    const createNewSession = useCallback(
        async (clearMessages = true) => {
            if (!token) return;

            try {
                const response = await fetch(`${api}/sessions`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to create session');
                }

                const newSession = await response.json();
                setCurrentSessionId(newSession.session_id);

                // Notify parent component about session change
                if (onSessionChange) {
                    onSessionChange(newSession.session_id, clearMessages);
                }

                // Refresh sessions list
                await fetchSessions();

                return newSession.session_id;
            } catch (err) {
                console.error('Failed to create new session:', err);
                return null;
            }
        },
        [api, token, fetchSessions, onSessionChange]
    );

    const loadSession = useCallback(
        async sessionId => {
            if (!token || !sessionId) return;

            try {
                const response = await fetch(`${api}/sessions/${sessionId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.ok) {
                    throw new Error('Failed to load session');
                }

                const data = await response.json();

                // Convert session messages to chat message format
                const loadedMessages = data.messages.map((msg, index) => {
                    return {
                        id: msg.id || `${msg.role}-${sessionId}-${index}`,
                        role: msg.role,
                        content: msg.content,
                        createdAt: new Date(msg.created_at * 1000), // Convert Unix timestamp to Date
                        isThinking: false,
                        isStreaming: false,
                        steps: [],
                        isHistorical: true, // Flag to indicate this is from history
                    };
                });

                setCurrentSessionId(sessionId);
                onSessionChange?.(sessionId, false, loadedMessages);

                return loadedMessages;
            } catch (err) {
                console.error('Failed to load session:', err);
                return null;
            }
        },
        [api, token, onSessionChange]
    );

    const deleteSession = useCallback(
        async sessionId => {
            if (!token || !sessionId) return false;

            try {
                const response = await fetch(`${api}/sessions/${sessionId}`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to delete session');
                }

                // If deleted session was current, clear it instead of creating a new one
                if (sessionId === currentSessionId) {
                    setCurrentSessionId(null);
                    onSessionChange?.(null, true);
                }

                await fetchSessions();

                return true;
            } catch (err) {
                console.error('Failed to delete session:', err);
                return false;
            }
        },
        [api, token, currentSessionId, onSessionChange, fetchSessions]
    );

    const updateSessionTitle = useCallback(
        async (sessionId, title) => {
            if (!token || !sessionId || !title) return false;

            // Optimistically update the UI immediately
            updateOptimisticSessions({ sessionId, title });

            try {
                const response = await fetch(`${api}/sessions/${sessionId}?title=${encodeURIComponent(title)}`, {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to update session title');
                }

                // Update the actual state after successful API call
                setSessions(prevSessions =>
                    prevSessions.map(session =>
                        session.session_id === sessionId
                            ? { ...session, title, updated_at: new Date().toISOString() }
                            : session
                    )
                );

                return true;
            } catch (err) {
                console.error('Failed to update session title:', err);

                // On failure, refetch sessions to get the correct state from server
                await fetchSessions();

                return false;
            }
        },
        [api, token, fetchSessions, updateOptimisticSessions]
    );

    return {
        currentSessionId,
        setCurrentSessionId,
        sessions: optimisticSessions,
        isLoadingSessions,
        fetchSessions,
        createNewSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    };
};

export default useChatSessions;
